import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Award, Heart, Shield, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const LOGO_SQ = "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-12/prod_agent_ead185b1-37d3-41b0-8463-37e266b01c59/Logo_Transparent_5507e2f6334644e387332c3baa0200d1.png";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: about */
export default function About() {
  const { t } = useLanguage();
  const values = [{ icon: <Award className="w-6 h-6" />, en: "Professional Excellence", ko: "전문적 탁월함", detailEn: "As an Enrolled Agent, we hold the highest IRS-recognized credential for tax professionals.", detailKo: "Enrolled Agent로서 세금 전문가 중 IRS가 인정하는 최고 자격을 보유하고 있습니다." },
    { icon: <Heart className="w-6 h-6" />, en: "Client-First Approach", ko: "고객 우선 접근", detailEn: "We explain complex tax matters in plain language — no jargon, no confusion.", detailKo: "복잡한 세금 문제를 평이한 언어로 설명합니다 — 전문 용어나 혼란 없이." },
    { icon: <Shield className="w-6 h-6" />, en: "Accuracy & Integrity", ko: "정확성 & 진실성", detailEn: "Every return is prepared with thorough care. We never compromise on accuracy or ethics.", detailKo: "모든 신고서는 철저히 준비됩니다. 정확성과 윤리를 절대 타협하지 않습니다." },
    { icon: <Users className="w-6 h-6" />, en: "Community Connection", ko: "커뮤니티 연결", detailEn: "We serve the Korean-American small business community with deep cultural understanding.", detailKo: "깊은 문화적 이해로 한인 스몰비즈니스 커뮤니티를 섬깁니다." },
  ];

  const credentials = [
    { en: "Enrolled Agent (EA) — IRS Authorized", ko: "Enrolled Agent (EA) — IRS 공인" },
    { en: "Federal Tax Law Specialist", ko: "연방 세법 전문가" },
    { en: "72-Hour Continuing Education (every 3 years)", ko: "3년마다 72시간 보수교육 이수" },
    { en: "IRS Unlimited Representation Rights", ko: "IRS 무제한 대리권" },
    { en: "Bilingual Service: English & Korean", ko: "이중언어 서비스: 영어 & 한국어" },
    { en: "Small Business & Individual Tax Specialist", ko: "스몰비즈니스 & 개인 세금 전문가" },
  ];

  return (
    <>
      <SEO
        title="About NexBiz Tax Advisor | Enrolled Agent San Jose"
        description="Meet your Enrolled Agent at NexBiz Tax Advisor. Serving individuals and small business owners in San Jose, Santa Clara, Sunnyvale, Cupertino, and throughout the Bay Area."
      />
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("About NexBiz Tax Advisor", "NexBiz Tax Advisor 소개")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t("Meet Your Enrolled Agent", "Enrolled Agent를 소개합니다")}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "Founded to help small business owners make better tax decisions with clarity and confidence.",
                "스몰비즈니스 오너들이 세금 문제를 더 명확하고 자신 있게 결정할 수 있도록 돕기 위해 설립되었습니다."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <h2 className="text-3xl font-bold text-foreground mb-6">{t("Our Story", "설립 이야기")}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {t(
                    "NexBiz Tax Advisor helps individuals and small business owners navigate tax compliance and tax planning with confidence. As an Enrolled Agent, we provide federal tax expertise, Tax Return preparation, planning, and IRS Notice support.",
                    "NexBiz Tax Advisor는 개인과 스몰비즈니스 오너들이 세금 컴플라이언스와 절세 계획을 자신 있게 진행할 수 있도록 돕습니다. Enrolled Agent로서 연방 세금 전문성, 세금보고, 절세 계획, IRS 통지서 대응 서비스를 제공합니다."
                  )}
                </p>
                <p>
                  {t(
                    "We serve clients in San Jose, Santa Clara, Sunnyvale, Cupertino, Mountain View, Campbell, and throughout the Bay Area — in English and Korean.",
                    "샌호세, 산타클라라, 서니베일, 쿠퍼티노, 마운틴뷰, 캠벨 및 베이 지역 전역의 고객에게 영어와 한국어로 서비스를 제공합니다."
                  )}
                </p>
                <p>
                  {t(
                    "We serve clients in San Jose, the Bay Area, and beyond — in English and Korean — with the same professional standard every time.",
                    "샌호세, 베이 에어리어 및 그 이상의 고객에게 영어와 한국어로 항상 동일한 전문적인 수준으로 서비스를 제공합니다."
                  )}
                </p>
              </div>
            </motion.div>
            <motion.div variants={fadeUp} className="flex justify-center lg:justify-end">
              <div className="bg-muted rounded-2xl p-8 flex flex-col items-center gap-6 w-full max-w-xs">
                <img
                  src={LOGO_SQ}
                  alt="NexBiz Tax Advisor"
                  className="w-28 h-28 object-contain"
                />
                <div className="text-center">
                  <p className="font-bold text-foreground text-lg">NexBiz Tax Advisor</p>
                  <p className="text-muted-foreground text-sm">San Jose, California</p>
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-accent/10 rounded-full text-xs font-semibold text-accent">
                    <Award className="w-3.5 h-3.5" />
                    {t("Enrolled Agent", "Enrolled Agent")}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-muted py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("Credentials & Qualifications", "자격 및 인증")}
            </motion.h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {credentials.map((c, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-center gap-3 bg-white rounded-xl border border-border p-4">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="text-sm font-medium text-foreground">{t(c.en, c.ko)}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("Our Values", "우리의 가치")}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {values.map((v, i) => (
                <motion.div key={i} variants={fadeUp} className="p-6 bg-card border border-border rounded-xl hover:border-accent/40 hover:shadow-sm transition-all">
                  <div className="w-12 h-12 rounded-xl bg-primary/8 flex items-center justify-center text-primary mb-4">
                    {v.icon}
                  </div>
                  <h3 className="font-bold text-foreground text-lg mb-2">{t(v.en, v.ko)}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t(v.detailEn, v.detailKo)}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-primary-foreground mb-4">
              {t("Let's Work Together", "함께 일해 봅시다")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 mb-8">
              {t(
                "Ready to experience tax clarity? Schedule a consultation today.",
                "세금 명확성을 경험할 준비가 되셨나요? 지금 상담을 예약하세요."
              )}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-accent text-white font-semibold hover:bg-accent/90 transition-colors shadow-md">
                {t("Schedule a Consultation", "상담 예약하기")} <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
